import { getCurrentInstance, ref } from 'vue';
import { AlertType } from '@/interfaces/IAlert';
import { IInvoicingItem } from '@/interfaces/IInvoicingItem';
import { IOrder } from '@/interfaces/IOrder';
import { PrinterType } from '@/modules/PrintService';
import { roundPrice } from '@/utils';
import { orderInvoiceableTotal } from '@/utils/orders.utils';
import CreateInvoiceModalArg from '@/components/modals/order/CreateInvoiceModalArg.vue';
import { useInvoiceAttempt } from './use-invoice-attempt';
import { useModal } from './use-modal';
import { usePrinting } from './use-printing';
import { useUi } from './use-ui';

/**
 * Shared mass-invoicing prompt flow (extracted from PosV2TableFooter):
 * shows CreateInvoiceModalArg with the comps-exclusion summary, issues via
 * useInvoiceAttempt.massCreateInvoice, stamps the orders and prints the
 * document when a ticket printer is connected. Must be called from a
 * component setup context (uses the proxy for i18n).
 */
export function useMassInvoice() {
  const modals = useModal();
  const ui = useUi();
  const printing = usePrinting();
  const invoiceAttempt = useInvoiceAttempt();

  // vue-i18n in script (Vue 2.7): translate through the component proxy.
  const proxy = getCurrentInstance()!.proxy;
  function i18nT(key: string, params?: Record<string, unknown>): string {
    return String(proxy.$t(key, params));
  }

  const invoiceLoading = ref(false);

  async function promptAndInvoice(orders: IOrder[]): Promise<void> {
    if (!orders.length || invoiceLoading.value) return;

    // Invoice transparency: what will actually be invoiced, with the comps
    // (invitación/empleado) exclusion surfaced before emitting.
    const subtotal = roundPrice(
      orders.reduce((acc, o) => acc + Number(o.total ?? 0), 0)
    );
    const invoiceTotal = roundPrice(
      orders.reduce((acc, o) => acc + orderInvoiceableTotal(o), 0)
    );
    const summary = {
      ordersCount: orders.length,
      subtotal,
      excludedTotal: roundPrice(subtotal - invoiceTotal),
      invoiceTotal,
    };

    let invoicingItem: IInvoicingItem | null = null;
    try {
      const invoiceData = await modals.showDynamicDialog<{
        invoiceType: number;
        idType: number;
        id: string;
      } | null>(CreateInvoiceModalArg, { summary }, 'create-invoice');
      if (!invoiceData) return;

      invoiceLoading.value = true;
      // Settles in the background if the user navigates away; this
      // continuation still runs on terminal (stamp + print + toast).
      invoicingItem = await invoiceAttempt.massCreateInvoice(
        orders.map((o) => o.id),
        invoiceData
      );

      for (const order of orders) {
        order.invoicingItemId = invoicingItem.id;
      }
      ui.showToast(AlertType.Success, i18nT('posV2.invoiceCreated'));
    } catch (e) {
      ui.serverError(e, i18nT('posV2.invoiceError'));
    } finally {
      invoiceLoading.value = false;
    }

    if (invoicingItem && printing.isPrinterConnected(PrinterType.Ticket)) {
      printing.printInvoice(invoicingItem).catch((e) => {
        ui.serverError(e, i18nT('posV2.invoicePrintError'));
      });
    }
  }

  return {
    invoiceLoading,
    promptAndInvoice,
  };
}
